import moment from "moment";

/**
 * Date and Time Utilities
 * Centralized utilities for date/time formatting and manipulation across the platform
 */

// Date format constants
export const DATE_FORMATS = {
  DISPLAY: "DD-MM-YYYY",                    // For tables, lists, and general display
  INPUT: "DD/MM/YYYY",                       // For date picker inputs
  STORAGE: "YYYY-MM-DD",                     // For database storage
  DATETIME_DISPLAY: "DD-MM-YYYY HH:mm A",   // For date-time display
  DATETIME_STORAGE: "YYYY-MM-DD HH:mm:ss",   // For database date-time storage
  TIME_DISPLAY: "HH:mm A",                   // For time-only display
  YEAR: "YYYY",                              // For year-only
  MONTH_YEAR: "MMM YYYY",                    // For month and year
  FULL_DATE: "dddd, MMMM DD, YYYY",         // For full date with day name
};

/**
 * Formats a date for display in tables and lists (DD-MM-YYYY)
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @param {string} fallback - Fallback value if date is invalid (default: "-")
 * @returns {string} Formatted date string or fallback
 */
export function formatDateForDisplay(date, fallback = "-") {
  if (!date || date === "" || date === null || date === undefined) {
    return fallback;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(DATE_FORMATS.DISPLAY) : fallback;
}

/**
 * Formats a date for date picker input (DD/MM/YYYY)
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @returns {string|null} Formatted date string or null if invalid
 */
export function formatDateForInput(date) {
  if (!date || date === "" || date === null || date === undefined) {
    return null;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(DATE_FORMATS.INPUT) : null;
}

/**
 * Formats a date for database storage (YYYY-MM-DD)
 * Handles Excel serial numbers and various date string formats
 * @param {string|Date|moment.Moment|number} date - Date to format (can be Excel serial number)
 * @param {string} typeFormat - Expected date format from SETA JSON typeFormat field (optional)
 * @returns {string} Formatted date string in YYYY-MM-DD format or empty string if invalid
 */
export function formatDateForStorage(date, typeFormat = null) {
  if (!date || date === "" || date === null || date === undefined) {
    return "";
  }

  // If it's already a valid date string in YYYY-MM-DD format, return as is
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // First, try to parse as Excel serial number (numeric value)
  // Excel sometimes stores dates as numeric serial numbers but displays them as formatted dates
  // We need to check this first before trying string formats
  const numericValue = typeof date === "number" ? date : (!isNaN(date) && !isNaN(parseFloat(date)) ? parseFloat(date) : null);
  
  if (numericValue !== null) {
    // Check if it's a valid Excel serial number (typically between 1 and ~50000 for dates from 1900-2100)
    // Excel serial date: days since January 1, 1900
    if (numericValue >= 1 && numericValue <= 100000) {
      // Excel incorrectly treats 1900 as a leap year, so we need to adjust
      const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
      const days = numericValue;
      const dateObj = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
      if (!isNaN(dateObj.getTime())) {
        const parsed = moment(dateObj);
        if (parsed.isValid()) {
          return parsed.format(DATE_FORMATS.STORAGE);
        }
      }
    }
  }

  // If typeFormat is provided from SETA JSON, try parsing with that format first
  if (typeFormat && typeof date === "string") {
    const parsed = moment(date, typeFormat, true);
    if (parsed.isValid()) {
      return parsed.format(DATE_FORMATS.STORAGE);
    }
  }

  // Try to parse as various date string formats
  const dateFormats = [
    "DD/MM/YYYY",
    "DD-MM-YYYY",
    "MM/DD/YYYY",
    "MM-DD-YYYY",
    "YYYY/MM/DD",
    "YYYY-MM-DD",
    "DD/MM/YY",
    "DD-MM-YY",
    "MM/DD/YY",
    "MM-DD-YY",
    "DD MMM YYYY",
    "DD MMMM YYYY",
    "MMM DD, YYYY",
    "MMMM DD, YYYY",
  ];

  for (const format of dateFormats) {
    const parsed = moment(date, format, true);
    if (parsed.isValid()) {
      return parsed.format(DATE_FORMATS.STORAGE);
    }
  }

  // Try moment's default parsing
  const defaultParsed = moment(date);
  if (defaultParsed.isValid()) {
    return defaultParsed.format(DATE_FORMATS.STORAGE);
  }

  // If all parsing fails, return empty string
  console.warn(`Could not parse date: ${date}${typeFormat ? ` with format ${typeFormat}` : ""}`);
  return "";
}

/**
 * Parses and normalizes Excel dates to YYYY-MM-DD format
 * Handles Excel serial numbers and various date string formats
 * Uses typeFormat from SETA JSON if provided to parse dates in the expected format
 * @param {string|number} value - Date value from Excel (can be numeric serial number or formatted string)
 * @param {string} fieldName - Field name for error logging (optional)
 * @param {string} typeFormat - Expected date format from SETA JSON typeFormat field (optional)
 * @returns {string} Normalized date in YYYY-MM-DD format or empty string if invalid
 */
export function parseExcelDate(value, fieldName = "", typeFormat = null) {
  return formatDateForStorage(value, typeFormat);
}

/**
 * Formats a date-time for display (DD-MM-YYYY HH:mm A)
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @param {string} fallback - Fallback value if date is invalid (default: "-")
 * @returns {string} Formatted date-time string or fallback
 */
export function formatDateTimeForDisplay(date, fallback = "-") {
  if (!date || date === "" || date === null || date === undefined) {
    return fallback;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(DATE_FORMATS.DATETIME_DISPLAY) : fallback;
}

/**
 * Formats a date-time for database storage (YYYY-MM-DD HH:mm:ss)
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @returns {string} Formatted date-time string or empty string if invalid
 */
export function formatDateTimeForStorage(date) {
  if (!date || date === "" || date === null || date === undefined) {
    return "";
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(DATE_FORMATS.DATETIME_STORAGE) : "";
}

/**
 * Formats time only for display (HH:mm A)
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @param {string} fallback - Fallback value if date is invalid (default: "-")
 * @returns {string} Formatted time string or fallback
 */
export function formatTimeForDisplay(date, fallback = "-") {
  if (!date || date === "" || date === null || date === undefined) {
    return fallback;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(DATE_FORMATS.TIME_DISPLAY) : fallback;
}

/**
 * Formats year only (YYYY)
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @returns {string} Year string or empty string if invalid
 */
export function formatYear(date) {
  if (!date || date === "" || date === null || date === undefined) {
    return "";
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(DATE_FORMATS.YEAR) : "";
}

/**
 * Formats month and year (MMM YYYY)
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @param {string} fallback - Fallback value if date is invalid (default: "-")
 * @returns {string} Formatted month-year string or fallback
 */
export function formatMonthYear(date, fallback = "-") {
  if (!date || date === "" || date === null || date === undefined) {
    return fallback;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(DATE_FORMATS.MONTH_YEAR) : fallback;
}

/**
 * Formats full date with day name (dddd, MMMM DD, YYYY)
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @param {string} fallback - Fallback value if date is invalid (default: "-")
 * @returns {string} Formatted full date string or fallback
 */
export function formatFullDate(date, fallback = "-") {
  if (!date || date === "" || date === null || date === undefined) {
    return fallback;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(DATE_FORMATS.FULL_DATE) : fallback;
}

/**
 * Gets current date in ISO string format
 * @returns {string} Current date as ISO string
 */
export function currentDate() {
  return new Date().toISOString();
}

/**
 * Converts a date to ISO string format
 * @param {string|Date|moment.Moment|number} date - Date to convert
 * @returns {string} ISO string or empty string if invalid
 */
export function getDate(date) {
  if (!date || date === "" || date === null || date === undefined) {
    return "";
  }
  try {
    return new Date(date).toISOString();
  } catch (error) {
    console.warn(`Could not convert date to ISO: ${date}`);
    return "";
  }
}

/**
 * Get a moment object set to the end of the day for a given ISO date string
 * @param {string} isoString - ISO date string
 * @returns {moment.Moment|null} Moment object at end of day or null if invalid
 */
export function getEndOfDayFromISOString(isoString) {
  if (!isoString) return null;
  const date = moment(isoString);
  return date.isValid() ? date.endOf('day') : null;
}

/**
 * Get a moment object set to the start of the day for a given date
 * @param {string|Date|moment.Moment|number} date - Date to process
 * @returns {moment.Moment|null} Moment object at start of day or null if invalid
 */
export function getStartOfDay(date) {
  if (!date || date === "" || date === null || date === undefined) {
    return null;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.startOf('day') : null;
}

/**
 * Get a moment object set to the end of the day for a given date
 * @param {string|Date|moment.Moment|number} date - Date to process
 * @returns {moment.Moment|null} Moment object at end of day or null if invalid
 */
export function getEndOfDay(date) {
  if (!date || date === "" || date === null || date === undefined) {
    return null;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.endOf('day') : null;
}

/**
 * Checks if a date is valid
 * @param {string|Date|moment.Moment|number} date - Date to validate
 * @returns {boolean} True if date is valid, false otherwise
 */
export function isValidDate(date) {
  if (!date || date === "" || date === null || date === undefined) {
    return false;
  }
  return moment(date).isValid();
}

/**
 * Compares two dates
 * @param {string|Date|moment.Moment|number} date1 - First date
 * @param {string|Date|moment.Moment|number} date2 - Second date
 * @param {string} unit - Unit of comparison ('year', 'month', 'day', 'hour', 'minute', 'second')
 * @returns {number} -1 if date1 is before date2, 0 if equal, 1 if date1 is after date2
 */
export function compareDates(date1, date2, unit = 'day') {
  const moment1 = moment(date1);
  const moment2 = moment(date2);
  
  if (!moment1.isValid() || !moment2.isValid()) {
    return null;
  }
  
  if (moment1.isBefore(moment2, unit)) return -1;
  if (moment1.isAfter(moment2, unit)) return 1;
  return 0;
}

/**
 * Checks if a date is before another date
 * @param {string|Date|moment.Moment|number} date1 - First date
 * @param {string|Date|moment.Moment|number} date2 - Second date
 * @param {string} unit - Unit of comparison ('year', 'month', 'day', 'hour', 'minute', 'second')
 * @returns {boolean} True if date1 is before date2
 */
export function isDateBefore(date1, date2, unit = 'day') {
  const moment1 = moment(date1);
  const moment2 = moment(date2);
  
  if (!moment1.isValid() || !moment2.isValid()) {
    return false;
  }
  
  return moment1.isBefore(moment2, unit);
}

/**
 * Checks if a date is after another date
 * @param {string|Date|moment.Moment|number} date1 - First date
 * @param {string|Date|moment.Moment|number} date2 - Second date
 * @param {string} unit - Unit of comparison ('year', 'month', 'day', 'hour', 'minute', 'second')
 * @returns {boolean} True if date1 is after date2
 */
export function isDateAfter(date1, date2, unit = 'day') {
  const moment1 = moment(date1);
  const moment2 = moment(date2);
  
  if (!moment1.isValid() || !moment2.isValid()) {
    return false;
  }
  
  return moment1.isAfter(moment2, unit);
}

/**
 * Checks if a date is between two dates (inclusive)
 * @param {string|Date|moment.Moment|number} date - Date to check
 * @param {string|Date|moment.Moment|number} startDate - Start date
 * @param {string|Date|moment.Moment|number} endDate - End date
 * @param {string} unit - Unit of comparison ('year', 'month', 'day', 'hour', 'minute', 'second')
 * @param {string} inclusivity - '[]' for inclusive, '()' for exclusive, '[)' or '(]' for mixed
 * @returns {boolean} True if date is between startDate and endDate
 */
export function isDateBetween(date, startDate, endDate, unit = 'day', inclusivity = '[]') {
  const momentDate = moment(date);
  const momentStart = moment(startDate);
  const momentEnd = moment(endDate);
  
  if (!momentDate.isValid() || !momentStart.isValid() || !momentEnd.isValid()) {
    return false;
  }
  
  return momentDate.isBetween(momentStart, momentEnd, unit, inclusivity);
}

/**
 * Adds time to a date
 * @param {string|Date|moment.Moment|number} date - Base date
 * @param {number} amount - Amount to add
 * @param {string} unit - Unit to add ('years', 'months', 'days', 'hours', 'minutes', 'seconds')
 * @returns {moment.Moment|null} New moment object or null if invalid
 */
export function addTime(date, amount, unit) {
  if (!date || date === "" || date === null || date === undefined) {
    return null;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.add(amount, unit) : null;
}

/**
 * Subtracts time from a date
 * @param {string|Date|moment.Moment|number} date - Base date
 * @param {number} amount - Amount to subtract
 * @param {string} unit - Unit to subtract ('years', 'months', 'days', 'hours', 'minutes', 'seconds')
 * @returns {moment.Moment|null} New moment object or null if invalid
 */
export function subtractTime(date, amount, unit) {
  if (!date || date === "" || date === null || date === undefined) {
    return null;
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.subtract(amount, unit) : null;
}

/**
 * Gets the difference between two dates
 * @param {string|Date|moment.Moment|number} date1 - First date
 * @param {string|Date|moment.Moment|number} date2 - Second date
 * @param {string} unit - Unit of difference ('years', 'months', 'days', 'hours', 'minutes', 'seconds')
 * @param {boolean} precise - If true, returns precise difference (default: false)
 * @returns {number|null} Difference in specified unit or null if invalid
 */
export function getDateDifference(date1, date2, unit = 'days', precise = false) {
  const moment1 = moment(date1);
  const moment2 = moment(date2);
  
  if (!moment1.isValid() || !moment2.isValid()) {
    return null;
  }
  
  return precise ? moment1.diff(moment2, unit, true) : moment1.diff(moment2, unit);
}

/**
 * Gets relative time from now (e.g., "2 days ago", "in 3 hours")
 * @param {string|Date|moment.Moment|number} date - Date to format
 * @returns {string} Relative time string or empty string if invalid
 */
export function getRelativeTime(date) {
  if (!date || date === "" || date === null || date === undefined) {
    return "";
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed.fromNow() : "";
}

/**
 * Gets humanized duration between two dates
 * @param {string|Date|moment.Moment|number} date1 - First date
 * @param {string|Date|moment.Moment|number} date2 - Second date
 * @returns {string} Humanized duration string or empty string if invalid
 */
export function getHumanizedDuration(date1, date2) {
  const moment1 = moment(date1);
  const moment2 = moment(date2);
  
  if (!moment1.isValid() || !moment2.isValid()) {
    return "";
  }
  
  return moment.duration(moment1.diff(moment2)).humanize();
}
